import {useCallback, useEffect, useMemo, useReducer, useRef} from 'react';
import type {HttpError} from 'api/http/types';
import type {Prize} from 'api/types';
import {deletePrize, getPrize, postPrize, putPrize} from 'api/prize';
import {useGetTokens} from 'services/auth';
import type {PrizeState} from '../reducer/prizeReducer';
import {PrizeDefaultState, prizeReducer} from '../reducer/prizeReducer';

export type SaveStatus = 'Saved' | 'Failed' | 'None';

export type SavePrizeFunc = (prize: Prize) => Promise<Prize>;

export type DeletePrizeFunc = (prizeId: string) => Promise<void>;

export const usePrize = (
  prizeId?: string | null,
  mock?: boolean,
): {
  prize: PrizeState;
  savePrize: SavePrizeFunc;
  deletePrize: DeletePrizeFunc;
} => {
  const getTokens = useGetTokens();
  const [prize, dispatch] = useReducer(prizeReducer, PrizeDefaultState);
  const prizeIdRef = useRef(prizeId);

  useEffect(() => {
    if (prize.status === 'NotLoaded') {
      if (prizeId) {
        dispatch({type: 'LOAD_PRIZE', loadType: 'REQUEST'});

        getPrize(prizeId, getTokens, mock)
          .then(data =>
            dispatch({
              type: 'LOAD_PRIZE',
              loadType: 'SUCCESS',
              payload: {data},
            }),
          )
          .catch((error: HttpError) =>
            dispatch({
              type: 'LOAD_PRIZE',
              loadType: 'FAILURE',
              payload: {error},
            }),
          );
      }
    }
  }, [prize.status, prizeId, getTokens, mock]);

  // Reset load when prizeId changes
  useEffect(() => {
    if (prizeId !== prizeIdRef.current) {
      prizeIdRef.current = prizeId;
      dispatch({type: 'LOAD_PRIZE', loadType: 'NOT_LOADED'});
    }
  });

  /** Save prize to backend and update reducer state if successful */
  const handleSavePrize = useCallback<SavePrizeFunc>(
    prize => {
      const apiCall: Promise<Prize> = prizeId
        ? putPrize(prize, getTokens, mock)
        : postPrize(prize, getTokens, mock);

      return apiCall.then(updatedPrize => {
        dispatch({
          type: 'SAVED_PRIZE',
          payload: {data: {...prize, ...updatedPrize}},
        });
        return updatedPrize;
      });
    },
    [prizeId, getTokens, mock],
  );

  /** Delete prize from backend and update reducer state if successful */
  const handleDeletePrize = useCallback<DeletePrizeFunc>(
    prizeId => {
      return deletePrize(prizeId, getTokens, mock).then(() => {
        dispatch({
          type: 'DELETED_PRIZE',
        });
      });
    },
    [getTokens, mock],
  );

  return useMemo(() => {
    return {
      prize,
      savePrize: handleSavePrize,
      deletePrize: handleDeletePrize,
    };
  }, [prize, handleSavePrize, handleDeletePrize]);
};
